import {
  PAY_OUT_LABELS,
  WRITE_OFF_LABEL,
  describeOverShort,
  formatInTimeZone,
  formatMinor,
} from '@didto/domain';
import type { PayOutCategory } from '@didto/domain';
import { KvRow, Stat } from '@didto/ui';
import type { ReactNode } from 'react';
import type { ShiftSummary } from '@/lib/shifts';
import './shift.css';

/**
 * A closed shift's summary (ADR-024): the drawer's cash from the opening count to what was counted
 * and kept, the totals by payment method, what anyone other than the cashier did with its money
 * (the owner at the till, shown apart), corrections, and payments that arrived after the close.
 * The cashier sees it on the tablet after the close; the owner's review pages reuse it (7A-2).
 */
const CLOSE_KIND: Record<string, string> = {
  cashier: 'Closed by the cashier',
  owner: 'Closed by the owner',
  handover: 'Handed over',
  takeover: 'Taken over',
};

export const ACTION_LABEL: Record<string, string> = {
  payment: 'Payment',
  void: 'Payment void',
  refund: 'Refund',
  pay_in: 'Pay-in',
  pay_out: 'Pay-out',
  safe_drop: 'Safe drop',
  no_sale: 'No sale',
  'order.void_line': 'Line voided',
  'order.void': 'Order voided',
  'payment.void': 'Payment voided',
  'order.refund': 'Refund',
  'ticket.unserve': 'Serve corrected',
  'order.unserve': 'Serve corrected',
  'table.force_clear': 'Table cleared by force',
  'payment.discard_queued': 'Unrecorded payment discarded',
  'cash.movement_void': 'Cash entry cancelled',
  'order.write_off': 'Written off',
  'order.write_off_reinstate': 'Write-off reinstated',
  'order.return_line': 'Item returned',
};

const FLAG_SOURCE: Record<string, string> = {
  counter: 'confirmed at the counter',
  queued: 'sent late, not confirmed at the counter',
  owner_entry: 'recorded by the owner',
};

/**
 * The tone of a drawer's difference, always shown with its words (describeOverShort): balanced is
 * success; over or short within the count tolerance is accepted and needs no one (neutral); beyond
 * it, danger.
 */
export function overShortTone(
  minor: number,
  toleranceMinor: number,
): 'success' | 'neutral' | 'danger' {
  if (minor === 0) return 'success';
  return Math.abs(minor) <= toleranceMinor ? 'neutral' : 'danger';
}

/** A figure: its label, and the amount in tabular figures. */
function Line({ label, value, total }: { label: ReactNode; value: string; total?: boolean }) {
  return <KvRow label={label} value={<span className="d-mono">{value}</span>} total={!!total} />;
}

/** Something that happened, in words that wrap, with its amount on the right. */
function Item({ children, amount }: { children: ReactNode; amount: string }) {
  return (
    <div className="d-kv__row shift-summary__item">
      <span>{children}</span>
      <span className="d-mono">{amount}</span>
    </div>
  );
}

export function ShiftSummaryView({
  summary,
  live,
  currency,
  timeZone,
}: {
  summary: ShiftSummary;
  /** The figures now, when they differ from the frozen ones (payments that arrived late). */
  live?: ShiftSummary | null;
  currency: string;
  timeZone: string;
}) {
  const money = (minor: number) => formatMinor(minor, currency);
  const at = (iso: string | null) => (iso ? formatInTimeZone(iso, timeZone) : '—');
  const sh = summary.shift;
  const c = summary.cash;
  const tolerance = c.tolerance_minor ?? 5000;
  const lateNow =
    live && live.cash.late_entries.count > c.late_entries.count ? live.cash.late_entries : null;
  const removedTo =
    c.removed_to === 'safe' ? 'into the safe' : c.removed_to === 'owner' ? 'to the owner' : '';
  const closeFigures =
    c.close_expected_minor !== null || c.counted_minor !== null || c.over_short_minor !== null;
  return (
    <div className="d-stack" data-testid="shift-summary">
      <div className="shift-summary__head">
        <div className="shift-summary__title">
          {sh.drawer?.name ?? 'Drawer'} · {sh.cashier.name}
        </div>
        <div className="shift-summary__meta">
          {at(sh.opened_at)} → {at(sh.closed_at)}
          {sh.close_kind ? ` · ${CLOSE_KIND[sh.close_kind] ?? sh.close_kind}` : ''}
          {sh.closed_by && sh.closed_by.id !== sh.cashier.id ? ` (${sh.closed_by.name})` : ''}
        </div>
      </div>

      <section
        className="d-kv shift-summary__section"
        aria-label="Cash in the drawer"
        data-testid="summary-cash"
      >
        <div className="d-eyebrow">Cash in the drawer</div>
        {closeFigures && (
          <div className="d-stats">
            {c.close_expected_minor !== null && (
              <Stat label="Expected" value={money(c.close_expected_minor)} />
            )}
            {c.counted_minor !== null && <Stat label="Counted" value={money(c.counted_minor)} />}
            {c.over_short_minor !== null && (
              <Stat
                label="Difference"
                value={describeOverShort(c.over_short_minor, money)}
                tone={overShortTone(c.over_short_minor, tolerance)}
                testId="summary-over-short"
              />
            )}
          </div>
        )}
        {c.variance_reason && <p className="shift-summary__quote">“{c.variance_reason}”</p>}
        <Line label="Opening count" value={money(c.opening_count_minor)} />
        <Line label="Cash sales" value={`+ ${money(c.cash_sales_minor)}`} />
        {c.cash_returned_minor > 0 && (
          <Line label="Cash given back" value={`− ${money(c.cash_returned_minor)}`} />
        )}
        {c.pay_ins_minor !== 0 && <Line label="Pay-ins" value={`+ ${money(c.pay_ins_minor)}`} />}
        {c.pay_outs_minor !== 0 && <Line label="Pay-outs" value={`− ${money(c.pay_outs_minor)}`} />}
        {c.safe_drops_minor > 0 && (
          <Line label="Safe drops" value={`− ${money(c.safe_drops_minor)}`} />
        )}
        {c.close_expected_minor !== null && (
          <Line label="Expected" value={money(c.close_expected_minor)} total />
        )}
        {c.retained_minor !== null && (
          <Line label="Kept in the drawer" value={money(c.retained_minor)} />
        )}
        {(c.removed_minor ?? 0) > 0 && (
          <Line label={`Taken out ${removedTo}`.trim()} value={money(c.removed_minor ?? 0)} />
        )}
        {c.opening_difference_minor !== null && c.opening_difference_minor !== 0 && (
          <p className="d-help">
            The opening count differed from what the previous shift kept by{' '}
            {describeOverShort(c.opening_difference_minor, money)}.
          </p>
        )}
      </section>

      {c.pay_outs_minor !== 0 && Object.keys(c.pay_outs_by_category).length > 0 && (
        <section className="d-kv shift-summary__section" aria-label="Pay-outs">
          <div className="d-eyebrow">Pay-outs</div>
          {Object.entries(c.pay_outs_by_category).map(([k, v]) => (
            <Line key={k} label={PAY_OUT_LABELS[k as PayOutCategory] ?? k} value={money(v)} />
          ))}
        </section>
      )}

      <section
        className="d-kv shift-summary__section"
        aria-label="By payment method"
        data-testid="summary-methods"
      >
        <div className="d-eyebrow">By payment method</div>
        {summary.methods.length === 0 && <p className="d-muted">No payments.</p>}
        {summary.methods.map((x) => (
          <Line
            key={x.method}
            label={`${x.method} (${x.count})`}
            value={
              x.voided_minor + x.refunded_minor > 0
                ? `${money(x.net_minor)} (of ${money(x.captured_minor)})`
                : money(x.net_minor)
            }
          />
        ))}
      </section>

      {summary.others.length > 0 && (
        <section
          className="d-kv shift-summary__section"
          aria-label="Owner actions"
          data-testid="summary-others"
        >
          <div className="d-eyebrow">By someone else on this shift</div>
          {summary.others.map((x, i) => (
            <Item key={`${x.at}:${i}`} amount={money(x.amount_minor)}>
              {ACTION_LABEL[x.action] ?? x.action}
              {x.method ? ` · ${x.method}` : ''} · {x.by ?? '—'}
            </Item>
          ))}
        </section>
      )}

      {summary.corrections.length > 0 && (
        <section className="d-kv shift-summary__section" aria-label="Corrections">
          <div className="d-eyebrow">Corrections</div>
          {summary.corrections.map((x, i) => (
            <Item key={`${x.at}:${i}`} amount={x.amount_minor ? money(x.amount_minor) : ''}>
              {ACTION_LABEL[x.action] ?? x.action} · {x.by ?? '—'}
              {x.approver ? ` (approved by ${x.approver})` : ''}
              {x.reason ? ` · “${x.reason}”` : ''}
            </Item>
          ))}
        </section>
      )}

      {summary.write_offs && (
        <section
          className="d-kv shift-summary__section"
          aria-label="Written off"
          data-testid="summary-write-offs"
        >
          <div className="d-eyebrow">Written off (no cash)</div>
          {summary.write_offs.items.map((x) => (
            <Item key={x.id} amount={money(x.amount_minor)}>
              {WRITE_OFF_LABEL[x.kind]} · {x.order_number}
              {x.employee ? ` · ${x.employee}` : ''} · {x.by ?? '—'}
              {x.approver ? ` (approved by ${x.approver})` : ''}
              {x.reinstated ? ' · reinstated' : ''} · “{x.reason}”
            </Item>
          ))}
        </section>
      )}

      {summary.returns && (
        <section
          className="d-kv shift-summary__section"
          aria-label="Returned"
          data-testid="summary-returns"
        >
          <div className="d-eyebrow">Returned items (off the orders; nothing restocked)</div>
          {summary.returns.items.map((x) => (
            <Item key={x.id} amount={money(x.amount_minor)}>
              {x.qty} × {x.line} · {x.order_number} · {x.by ?? '—'}
              {x.approver ? ` (approved by ${x.approver})` : ''} · “{x.reason}”
              {x.refunded_minor > 0 ? ` · ${money(x.refunded_minor)} given back` : ''}
            </Item>
          ))}
        </section>
      )}

      {summary.reference_flags && summary.reference_flags.length > 0 && (
        <section
          className="d-kv shift-summary__section"
          aria-label="References to check"
          data-testid="summary-reference-flags"
        >
          <div className="d-eyebrow">References to check (for the owner’s review)</div>
          {summary.reference_flags.map((x) => (
            <Item key={x.payment_id} amount={money(x.amount_minor)}>
              {x.method} …{x.reference_last4} · {x.order_number} ·{' '}
              {x.matches > 0 ? 'used before' : ''}
              {x.matches > 0 && x.format_mismatch ? ' and ' : ''}
              {x.format_mismatch ? 'not in the method’s format' : ''} ·{' '}
              {FLAG_SOURCE[x.source] ?? x.source} · {x.by ?? '—'} · “{x.reason}”
            </Item>
          ))}
        </section>
      )}

      <div className="d-stack d-stack--tight shift-summary__section">
        {(c.late_entries.count > 0 || lateNow) && (
          <p className="d-help" data-testid="summary-late">
            Payments sent after the count started: {(lateNow ?? c.late_entries).count} (
            {money((lateNow ?? c.late_entries).amount_minor)}).
            {lateNow ? ' Some arrived after this summary was made.' : ''}
          </p>
        )}
        {summary.discarded.length > 0 && (
          <p className="d-help">
            Payments not recorded and discarded: {summary.discarded.length} (
            {money(summary.discarded.reduce((a, x) => a + (x.amount_minor ?? 0), 0))}).
          </p>
        )}
        <p className="d-faint">
          Orders: {summary.orders.placed} placed · {summary.orders.voided} voided
          {summary.orders.open_unpaid > 0 ? ` · ${summary.orders.open_unpaid} still unpaid` : ''}
        </p>
      </div>
    </div>
  );
}
